"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * GenerationTimeline — system-driven phase list for long AI generation waits.
 * Each phase ticks through pending → active (spinner) → completed (check),
 * with optional timed reassurance copy for the long phases so the wait reads
 * as progress instead of a stall.
 *
 * Ported from Creator Studio v2 (`src/components/create/GenerationTimeline.tsx`)
 * and re-themed: inline navy hexes replaced with `--accent` / `--muted` /
 * `--foreground` tokens; the local `@keyframes` block replaced with the
 * preset's `animate-spin` / `animate-in` / `animate-shimmer` utilities; the
 * hardcoded phase script is now a prop (`phases`), with the Creator Studio
 * script shipped as `DEFAULT_GENERATION_PHASES`.
 *
 * Pacing is presentational: phases advance on their own timers (duration +
 * jitter), independent of the real backend work. The standard pattern is to
 * run the timeline while the request is in flight and gate the reveal on
 * BOTH `onComplete` and the response landing.
 *
 * Sibling note: `<Stepper>` is the user-driven cousin (click/keyboard
 * navigation between steps); GenerationTimeline runs itself.
 *
 * Honours `prefers-reduced-motion` via `motion-reduce:` gates on the spinner,
 * shimmer bar, and entrance animations.
 *
 * @example
 *   <GenerationTimeline
 *     onComplete={() => setTimelineDone(true)}
 *     onPhaseChange={(i) => track("gen_phase", { i })}
 *   />
 *
 *   // Custom script:
 *   <GenerationTimeline
 *     phases={[
 *       { name: "Reading your sources", sub: "Scanning 12 documents", duration: 1500 },
 *       { name: "Drafting the summary", duration: 4000, hasProgress: true },
 *     ]}
 *   />
 */
export interface GenerationPhase {
  /** Headline shown for the phase. */
  name: string;
  /** Supporting line revealed once the phase is active. */
  sub?: string;
  /** Nominal phase duration in ms (jitter is added on top). */
  duration: number;
  /** Render an indeterminate shimmer bar while this phase is active. */
  hasProgress?: boolean;
  /**
   * Timed reassurance lines for anxiety management on long phases. Each
   * message swaps in `afterMs` after the phase starts and clears when the
   * phase completes.
   */
  reassurances?: readonly { afterMs: number; message: string }[];
}

/** The Creator Studio v2 "create an experience" script. */
export const DEFAULT_GENERATION_PHASES: readonly GenerationPhase[] = [
  { name: "Reading your idea", sub: "Making sense of what you want to create", duration: 1000 },
  { name: "Building the structure", sub: "Mapping out sections and chapters", duration: 2000 },
  {
    name: "Writing your content",
    sub: "This takes a moment. Longer content gets better results.",
    duration: 4500,
    hasProgress: true,
    reassurances: [
      { afterMs: 5000, message: "✦ Taking a moment. Longer content gets better results." },
      { afterMs: 8000, message: "✦ Still writing. We're being thorough, and it's worth the wait." },
    ],
  },
  { name: "Naming and branding it", sub: "We'll suggest a name, description, and logo", duration: 2000 },
  { name: "Getting your preview ready", sub: "Almost there", duration: 1500 },
];

type PhaseStatus = "pending" | "active" | "completed";

export interface GenerationTimelineProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Phase script. Pass a stable reference — a new array identity restarts
   * the timeline from phase 0. Default `DEFAULT_GENERATION_PHASES`.
   */
  phases?: readonly GenerationPhase[];
  /** Fired once, after the last phase completes. */
  onComplete?: () => void;
  /** Fired when a phase becomes active, with its index. */
  onPhaseChange?: (phaseIndex: number) => void;
  /** Max random ms added to each phase duration. Default 500. Set 0 for deterministic pacing. */
  jitter?: number;
}

export const GenerationTimeline = React.forwardRef<HTMLDivElement, GenerationTimelineProps>(
  (
    { phases = DEFAULT_GENERATION_PHASES, onComplete, onPhaseChange, jitter = 500, className, ...rest },
    ref,
  ) => {
    const [statuses, setStatuses] = React.useState<PhaseStatus[]>(() =>
      phases.map((_, i) => (i === 0 ? "active" : "pending")),
    );
    const [reassurance, setReassurance] = React.useState<string | null>(null);

    // Latest-ref the callbacks so consumers can pass inline closures without
    // restarting the timeline.
    const onCompleteRef = React.useRef(onComplete);
    const onPhaseChangeRef = React.useRef(onPhaseChange);
    React.useEffect(() => {
      onCompleteRef.current = onComplete;
      onPhaseChangeRef.current = onPhaseChange;
    });

    React.useEffect(() => {
      let completed = false;
      const timers: ReturnType<typeof setTimeout>[] = [];

      setStatuses(phases.map((_, i) => (i === 0 ? "active" : "pending")));
      setReassurance(null);

      const advance = (idx: number) => {
        if (idx >= phases.length) {
          if (!completed) {
            completed = true;
            onCompleteRef.current?.();
          }
          return;
        }

        setStatuses(phases.map((_, i) => (i < idx ? "completed" : i === idx ? "active" : "pending")));
        onPhaseChangeRef.current?.(idx);

        for (const { afterMs, message } of phases[idx]?.reassurances ?? []) {
          timers.push(setTimeout(() => setReassurance(message), afterMs));
        }

        const duration = (phases[idx]?.duration ?? 0) + Math.random() * jitter;
        timers.push(
          setTimeout(() => {
            if (phases[idx]?.reassurances?.length) setReassurance(null);
            advance(idx + 1);
          }, duration),
        );
      };

      advance(0);

      return () => {
        for (const t of timers) clearTimeout(t);
      };
    }, [phases, jitter]);

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label="Generation progress"
        className={cn("flex flex-col gap-5", className)}
        {...rest}
      >
        {phases.map((phase, i) => {
          const status = statuses[i] ?? "pending";
          return (
            <div key={i} className="flex items-start gap-3.5">
              {/* Status indicator */}
              <div className="flex w-6 shrink-0 justify-center pt-0.5">
                {status === "pending" && (
                  <span className="mt-1.5 size-2 rounded-full bg-primary/10" aria-hidden />
                )}
                {status === "active" && (
                  <span
                    className="size-5 rounded-full border-2 border-accent border-t-transparent animate-spin [animation-duration:800ms] motion-reduce:animate-none"
                    aria-hidden
                  />
                )}
                {status === "completed" && (
                  <span
                    className="flex size-5 items-center justify-center rounded-full bg-accent/10 animate-in zoom-in duration-200 motion-reduce:animate-none"
                    aria-hidden
                  >
                    <Check className="size-3 text-accent" />
                  </span>
                )}
              </div>

              {/* Phase text */}
              <div className="min-w-0 flex-1">
                <span
                  className={cn(
                    "text-sm font-medium transition-colors duration-200",
                    status === "pending" ? "text-muted-foreground/60" : "text-foreground",
                  )}
                >
                  {phase.name}
                </span>
                {phase.sub && status !== "pending" ? (
                  <span className="mt-0.5 block text-xs text-muted-foreground">{phase.sub}</span>
                ) : null}
                {phase.hasProgress && status === "active" ? (
                  <div className="mt-2 h-0.5 w-full overflow-hidden rounded-full bg-primary/10">
                    <div className="h-full w-full animate-shimmer bg-[linear-gradient(90deg,transparent,hsl(var(--accent)),transparent)] [background-size:200%_100%] motion-reduce:animate-none" />
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {/* Reassurance message */}
        {reassurance ? (
          <p className="text-xs italic text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none">
            {reassurance}
          </p>
        ) : null}
      </div>
    );
  },
);
GenerationTimeline.displayName = "GenerationTimeline";
